import { useState, useEffect, useCallback, useRef } from "react";
import Recording from "../components/Recording.jsx";
import SvgIcon from "../assest/svg/svg.jsx";
import SkeletonLoader from "../components/skeletonLoader.jsx";
import { useAppContext } from '../context/AppContext';

const API_BASE = "https://meeting.srptechs.com";

function formatDuration(seconds) {
    if (!seconds && seconds !== 0) return "—";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0
        ? `${m}m ${s.toString().padStart(2, "0")}s`
        : `${s}s`;
}

function Spinner() {
    return (
        <svg className="nb-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="green" strokeWidth="2.5">
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
    );
}

function MeetingRow({ meeting, isSelected, onDelete, isDeleting, style, fetchMeetings }) {

    const [isExpanded, setIsExpanded] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);
    const [activeTab, setActiveTab] = useState('summary');
    const [details, setDetails] = useState(null);
    const [retryId, setRetryId] = useState(null);
    const contentRef = useRef(null);
    const isFirstOpenRef = useRef(null);

    const speakers = meeting.speaker_names
        ? (typeof meeting.speaker_names === "string"
            ? meeting.speaker_names.split(",").map(s => s.trim())
            : meeting.speaker_names)
        : [];

    const formatDuration = (seconds) => {
        if (!seconds) return 'N/A';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        if (minutes === 0) return `${seconds} sec`;
        return `${minutes} min ${remainingSeconds} sec`;
    };

    const formatDateDetailed = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const fetchDetails = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/api/meetings/${meeting.id}`);
            setDetails(await res.json());
        } catch (error) {
            console.log(error);
        }
    }, [meeting.id]);

    useEffect(() => {
        if (isExpanded && contentRef.current) {
            const timeoutId = setTimeout(() => {
                if (contentRef.current) {
                    setContentHeight(contentRef.current.scrollHeight);
                    isFirstOpenRef.current = false;
                }
            }, 10);
            return () => clearTimeout(timeoutId);
        } else if (!isExpanded) {
            setContentHeight(0);
        }
    }, [isExpanded, details, activeTab])

    const handleRowClick = async () => {
        if (!isExpanded) {
            await fetchDetails();
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
        }
    };

    const handleRetry = async (id) => {
        setRetryId(id)
        try {
            const res = await fetch(`${API_BASE}/api/meetings/${id}/retry`, { method: 'POST' })
            await res.json();
            setRetryId(null)
            fetchMeetings()
        }
        catch (error) {
            setRetryId(null)
            console.log(error);
        }
    }

    const renderSummary = () => {

        const parseItem = (item) => {
            if (typeof item !== 'string') return item;

            try {
                // First try normal JSON.parse
                return JSON.parse(item);
            } catch (e) {
                try {
                    // More robust fix for Python-style dict strings
                    // This handles nested quotes by using a more careful approach
                    let fixed = item
                        // Replace outer single quotes with double quotes (but preserve inner ones)
                        .replace(/^'/, '"')
                        .replace(/'$/, '"')
                        // Replace single quotes around keys
                        .replace(/'([a-zA-Z_][a-zA-Z0-9_]*)':/g, '"$1":')
                        // Replace single quotes around string values, but not if they contain apostrophes
                        .replace(/:\s*'((?:[^'\\]|\\.)*)'/g, (match, content) => {
                            // Escape any double quotes inside
                            const escaped = content.replace(/"/g, '\\"');
                            return `: "${escaped}"`;
                        });

                    return JSON.parse(fixed);
                } catch (e2) {
                    // Ultimate fallback: manual extraction
                    const extractField = (field) => {
                        const regex = new RegExp(`'${field}':\\s*'([^']*)'`, 'i');
                        const match = item.match(regex);
                        return match ? match[1] : null;
                    };

                    // Check if it's a decision
                    if (item.includes('who') && item.includes('what')) {
                        return {
                            who: extractField('who'),
                            what: extractField('what'),
                            why: extractField('why')
                        };
                    }

                    // Check if it's a key point
                    if (item.includes('topic') && item.includes('person')) {
                        return {
                            topic: extractField('topic'),
                            person: extractField('person'),
                            detail: extractField('detail')
                        };
                    }

                    // Check if it's a risk
                    if (item.includes('risk') && item.includes('owner')) {
                        return {
                            risk: extractField('risk'),
                            owner: extractField('owner')
                        };
                    }

                    return { detail: item };
                }
            }
        };

        if (!details?.summary) return (
            <div className="text-center py-8 text-gray-500">
                No summary available for this meeting
            </div>
        );

        return (
            <div className="space-y-6">
                {/* Objective */}
                {details?.summary.objective && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="text-sm font-semibold text-blue-900 mb-1">Meeting Objective</h4>
                                <p className="text-gray-700 leading-relaxed">{details?.summary.objective}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Summary Text */}
                {details?.summary.summary_text && (
                    <div className="bg-white rounded-xl p-5 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            Meeting Summary
                        </h4>
                        <p className="text-gray-700 leading-relaxed">{details?.summary.summary_text}</p>
                    </div>
                )}

                {/* Key Points */}
                {details?.summary.key_points?.length > 0 && (
                    <div className="bg-green-50 rounded-xl p-5 border border-green-100">
                        <h4 className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Key Points
                        </h4>
                        <ul className="space-y-2">
                            {details.summary.key_points.map((point, idx) => {
                                const parsed = parseItem(point);
                                return (
                                    <li key={idx} className="flex items-start gap-2">
                                        <span className="text-green-600 mt-1">•</span>
                                        <span className="text-gray-700">
                                            <strong>{parsed.topic || 'General'}</strong> ({parsed.person || 'Unknown'}): {parsed.detail || JSON.stringify(parsed)}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {/* Decisions */}
                {details?.summary.decisions?.length > 0 && (
                    <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
                        <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Decisions Made
                        </h4>
                        <ul className="space-y-2">
                            {details.summary.decisions.map((decision, idx) => {
                                const parsed = parseItem(decision);
                                return (
                                    <li key={idx} className="flex items-start gap-2">
                                        <span className="text-purple-600 mt-1">✓</span>
                                        <span className="text-gray-700">
                                            <strong>{parsed.who || 'Team'}</strong> decided: {parsed.what || parsed.detail}
                                            {parsed.why && <span className="text-gray-500 text-sm block mt-1">Why: {parsed.why}</span>}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {/* Risks */}
                {details?.summary.risks?.length > 0 && (
                    <div className="bg-red-50 rounded-xl p-5 border border-red-100">
                        <h4 className="text-sm font-semibold text-red-900 mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Risks & Concerns
                        </h4>
                        <ul className="space-y-2">
                            {details.summary.risks.map((risk, idx) => {
                                const parsed = parseItem(risk);
                                return (
                                    <li key={idx} className="flex items-start gap-2">
                                        <span className="text-red-600 mt-1">⚠</span>
                                        <span className="text-gray-700">
                                            {parsed.risk || parsed.detail}
                                            {parsed.owner && <span className="text-red-600 text-sm block mt-1">Owner: {parsed.owner}</span>}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

            </div>
        );
    };

    const renderTranscript = () => {
        if (!details?.transcript || !details?.transcript.cleaned_text) {
            return (
                <div className="text-center py-8 text-gray-500">
                    No transcript available for this meeting
                </div>
            );
        }

        // Split transcript into speaker lines
        const lines = details?.transcript.cleaned_text.split('\n').filter(line => line.trim());

        return (
            <div className="space-y-4">
                {/* Transcript Stats */}
                <div className="flex gap-4 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                        </svg>
                        <span>{details?.transcript.word_count || 0} words</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>{details?.speaker_count || 1} speaker(s)</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{formatDuration(details?.duration_seconds)}</span>
                    </div>
                </div>

                {/* Transcript Content */}
                <div className="bg-gray-50 rounded-xl p-5 max-h-96 overflow-y-auto">
                    <div className="space-y-3">
                        {lines.map((line, idx) => {
                            const isSpeaker = line.includes(':');
                            if (!isSpeaker) return null;

                            const [speaker, ...textParts] = line.split(':');
                            const text = textParts.join(':').trim();

                            return (
                                <div key={idx} className="flex gap-3 hover:bg-white p-2 rounded-lg transition-colors">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <span className="text-xs font-semibold text-blue-700">
                                                {speaker.replace('Speaker', 'S')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <span className="text-sm font-semibold text-gray-700">{speaker}</span>
                                        <p className="text-gray-600 text-sm mt-1 leading-relaxed">{text}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const renderMetadata = () => {
        return (
            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Meeting ID */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Meeting ID</label>
                        <p className="text-sm text-gray-700 mt-1 font-mono">{details?.id}</p>
                    </div>

                    {/* Language */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Language</label>
                        <p className="text-sm text-gray-700 mt-1 flex items-center gap-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                {details?.language?.toUpperCase() || 'N/A'}
                            </span>
                        </p>
                    </div>

                    {/* Created At */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Created At</label>
                        <p className="text-sm text-gray-700 mt-1">{formatDateDetailed(details?.created_at)}</p>
                    </div>

                    {/* Completed At */}
                    {details?.completed_at && (
                        <div className="bg-gray-50 rounded-lg p-4">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Completed At</label>
                            <p className="text-sm text-gray-700 mt-1">{formatDateDetailed(details?.completed_at)}</p>
                        </div>
                    )}

                    {/* Processing Times */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Transcription Time</label>
                        <p className="text-sm text-gray-700 mt-1">{details?.transcription_duration?.toFixed(2)} seconds</p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Processing Time</label>
                        <p className="text-sm text-gray-700 mt-1">{details?.processing_duration?.toFixed(2)} seconds</p>
                    </div>
                </div>

                {speakers?.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Participants</label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {speakers?.map((speaker, idx) => (
                                <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                    {speaker}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                {details?.action_items && details?.action_items.length > 0 && (
                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                        <h4 className="text-sm font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Action Items
                        </h4>
                        <ul className="space-y-2">
                            {details?.action_items.map((item, idx) => (
                                <li key={idx} className="text-gray-700 text-sm">• {item.task}</li>
                            ))}
                        </ul>
                    </div>
                )}

            </div>
        );
    };

    return (
        <div
            className={`ml-row-enter relative mx-4 my-2 rounded-2xl transition-all duration-200 ${isSelected
                ? "shadow-[0_4px_16px_rgba(102,126,234,0.15),0_2px_4px_rgba(0,0,0,0.05)]"
                : "shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.05)] hover:-translate-y-0.5"
                }`}
            style={style}
        >
            <div
                className={`
                    flex items-center gap-4 w-full text-left p-4 rounded-2xl
                    transition-all duration-200 relative cursor-pointer border-none
                    bg-white
                `}
                onClick={handleRowClick}
            >
                {/* Expand/Collapse Icon */}
                <div className="flex-shrink-0 text-gray-400 transition-transform duration-200">
                    <svg
                        className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-4 mb-1 flex-wrap">
                        <p className="m-0 text-[15px] font-semibold text-[var(--color-text-primary)] truncate">
                            {meeting?.title}
                        </p>
                        {meeting?.status === "complete" ? (
                            <span className="px-4 py-1 rounded-full text-xs font-medium bg-[#8b3580] text-white whitespace-nowrap">
                                Complete
                            </span>
                        ) : meeting?.status === "transcribing" ? (
                            <Spinner />
                        ) : meeting?.status === "processing" ? (
                            <Spinner />
                        ) : meeting?.status === "failed" ? (
                            <span className="px-4 py-1 rounded-full text-xs font-medium bg-[#FA003F] text-white whitespace-nowrap">
                                Failed
                            </span>
                        ) : null}

                        {/* Duration Badge */}
                        {meeting?.duration_seconds > 0 && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 whitespace-nowrap">
                                {formatDuration(meeting?.duration_seconds)}
                            </span>
                        )}
                    </div>
                    <p className="m-0 text-xs text-[var(--color-text-secondary)] truncate">
                        {formatDateDetailed(meeting?.created_at || meeting?.date)}
                        {speakers.length > 0 && (
                            <span className="text-[var(--color-text-tertiary)]">
                                {" · "}{speakers.slice(0, 2).join(", ")}{speakers.length > 2 ? ` +${speakers.length - 2}` : ""}
                            </span>
                        )}
                    </p>
                </div>
            </div>

            {/* Delete Icon */}

            {meeting?.status === "failed" && (
                <button
                    onClick={() => {
                        handleRetry(meeting?.id)
                    }}
                    aria-label={`Delete ${details?.title || "recording"}`}
                    className={`
                    absolute top-4 right-14 bg-white border border-[green]
                    rounded-lg cursor-pointer p-1.5 flex items-center justify-center
                    text-red-500 transition-all duration-200 shadow-sm z-[2]
                    opacity-70 hover:opacity-100 hover:bg-red-50 hover:scale-105
                    disabled:cursor-not-allowed disabled:opacity-50
                `}
                >
                    {retryId === meeting?.id ? <Spinner /> : <SvgIcon name='reloadIcon' />}
                </button>
            )}

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete(meeting);
                }}
                disabled={isDeleting}
                aria-label={`Delete ${details?.title || "recording"}`}
                className={`
                    absolute top-4 right-3 bg-white border border-[var(--color-border-tertiary)]
                    rounded-lg cursor-pointer p-1.5 flex items-center justify-center
                    text-red-500 transition-all duration-200 shadow-sm z-[2]
                    opacity-70 hover:opacity-100 hover:bg-red-50 hover:scale-105
                    disabled:cursor-not-allowed disabled:opacity-50
                `}
            >
                <SvgIcon name='deleteIcon' />
            </button>

            {/* Accordion Content */}
            <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{ maxHeight: `${contentHeight}px` }}
            >
                <div ref={contentRef} className="px-4 pb-4 pt-2">
                    {/* Tabs */}
                    <div className="flex gap-2 border-b border-gray-200 mb-6">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab('summary');
                            }}
                            className={`px-4 py-2 text-sm font-medium transition-all duration-200 relative ${activeTab === 'summary'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Summary
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab('transcript');
                            }}
                            className={`px-4 py-2 text-sm font-medium transition-all duration-200 relative ${activeTab === 'transcript'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Transcript
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab('details');
                            }}
                            className={`px-4 py-2 text-sm font-medium transition-all duration-200 relative ${activeTab === 'details'
                                ? 'text-blue-600 border-b-2 border-blue-600'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Details
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="mt-4">
                        {activeTab === 'summary' && renderSummary()}
                        {activeTab === 'transcript' && renderTranscript()}
                        {activeTab === 'details' && renderMetadata()}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function MeetingsList({ dir }) {

    const [selected, setSelected] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const { meetings, loading, error, fetchMeetings } = useAppContext();

    useEffect(() => {
        const updateSidebarState = () => {
            const sidebarState = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-open');
            setSidebarOpen(sidebarState === '1');
        };

        updateSidebarState();

        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        const observer = new MutationObserver(updateSidebarState);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        fetchMeetings();
    }, [fetchMeetings]);

    const handleDelete = async (meeting) => {
        try {
            await fetch(`${API_BASE}/api/meetings/${meeting.id}`, { method: "DELETE" });
            fetchMeetings()
            if (selected?.id === meeting.id) setSelected(null);
        } catch {
            alert("Could not delete. Please try again.");
        }
    };

    const totalDuration = meetings.reduce((acc, m) => acc + (m.duration || 0), 0);
    const avgDuration = meetings.length > 0 ? Math.round(totalDuration / meetings.length) : 0;

    const getContentPadding = () => {
        if (isMobile) {
            return "";
        }
        return sidebarOpen ? (dir === 'rtl' ? 'pr-64' : 'pl-64') : (dir === 'rtl' ? 'pr-20' : 'pl-20');
    };

    return (
        <>
            <Recording />

            <div className={`transition-all duration-300 ${getContentPadding()}`}>

                {/* ── Premium Header ── */}
                <div style={{
                    padding: "20px 32px",
                    borderBottom: "1px solid var(--color-border-tertiary)",
                    background: "var(--color-background-primary)",
                    flexShrink: 0
                }}>
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        marginBottom: 20
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: "14px",
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)"
                            }}>
                                <i className="ti ti-microphone" aria-hidden="true"
                                    style={{ fontSize: 22, color: "white" }} />
                            </div>
                            <div>
                                <h1 style={{
                                    margin: 0, fontSize: 22, fontWeight: 700,
                                    color: "var(--color-text-primary)",
                                    letterSpacing: "-0.02em"
                                }}>Meeting Recordings</h1>
                                <p style={{
                                    margin: "4px 0 0", fontSize: 13,
                                    color: "var(--color-text-secondary)"
                                }}>Manage and analyze your meeting transcripts</p>
                            </div>
                        </div>

                    </div>

                    <div style={{
                        display: "flex",
                        gap: 24,
                        padding: "16px 0 8px"
                    }}>
                        {[
                            { label: "Total Recordings", value: meetings.length, icon: "ti-files" },
                            { label: "Total Duration", value: formatDuration(totalDuration), icon: "ti-clock" },
                            { label: "Average Duration", value: formatDuration(avgDuration), icon: "ti-chart-bar" }
                        ].map(stat => (
                            <div key={stat.label} style={{
                                flex: 1,
                                padding: "12px 16px",
                                background: "var(--color-background-secondary)",
                                borderRadius: "14px",
                                border: "1px solid var(--color-border-tertiary)",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.03)",
                                transition: "box-shadow 0.2s ease, transform 0.2s ease"
                            }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)";
                                    e.currentTarget.style.transform = "translateY(-1px)";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.03)";
                                    e.currentTarget.style.transform = "translateY(0)";
                                }}>
                                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                                    <i className={`ti ${stat.icon}`} style={{ fontSize: 16, color: "#667eea" }} />
                                    <p style={{ margin: 0, fontSize: 12, color: "var(--color-text-secondary)", fontWeight: 500 }}>
                                        {stat.label}
                                    </p>
                                </div>
                                <p style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "var(--color-text-primary)" }}>
                                    {stat.value}
                                </p>
                            </div>
                        ))}
                    </div>

                </div>

                {/* ── Body ── */}
                <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

                    {/* List panel */}
                    <div style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflowY: "auto",
                        minWidth: 0,
                        paddingLeft: '15px',
                        paddingRight: '15px',
                    }}>

                        {loading && (
                            <SkeletonLoader count={5} className="min-h-[70px]" style={{ borderRadius: '1rem' }} />
                        )}

                        {/* {!loading && error && <ErrorState message={error} onRetry={fetchMeetings} />}
                        {!loading && !error && <EmptyState />} */}

                        {!loading && !error && meetings?.map((meeting, i) => (
                            <MeetingRow
                                key={meeting.id}
                                meeting={meeting}
                                isSelected={selected?.id === meeting.id}
                                onClick={() => setSelected(prev => prev?.id === meeting.id ? null : meeting)}
                                style={{ animationDelay: `${Math.min(i * 30, 200)}ms` }}
                                onDelete={handleDelete}
                                fetchMeetings={fetchMeetings}
                            />
                        ))}
                    </div>

                    {/* Detail drawer */}
                    {/* {showDrawer && (
                        <DetailDrawer
                            meeting={selected}
                            onClose={() => setSelected(null)}
                        />
                    )} */}
                </div>
            </div>
        </>
    );
}